import React from 'react'
import { Box, TextField, Typography } from '@mui/material'
import useDomainGroup from './useDomainGroup'

const DOMAIN_PATTERN = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/

export const MAX_DOMAINS = 10

export const splitDomains = value => [
  ...new Set(
    (value || '')
      .toLowerCase()
      .split(/[;,\s]+/)
      .map(part => part.trim().replace(/^@+/, ''))
      .filter(Boolean)
  )
]

export const domainListError = value => {
  const domains = splitDomains(value)
  if (domains.length > MAX_DOMAINS) {
    return `You can add up to ${MAX_DOMAINS} email domains.`
  }
  const invalid = domains.find(domain => !DOMAIN_PATTERN.test(domain))
  return invalid ? `'${invalid}' isn't a valid email domain. For example: @example.com` : null
}

export const serverDomainError = error =>
  error?.response?.status === 422
    ? error.response.data?.errors?.domainRestriction?.[0] || error.response.data?.errors?.domain_restriction?.[0] || null
    : null

const joinDomains = domains => {
  const labels = domains.map(domain => `@${domain}`)
  return labels.length > 1 ? `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}` : labels[0]
}

const DomainListField = ({ value, onChange, userDomain, enabled = true, serverError }) => {
  const typed = splitDomains(value)
  const { domains, groups } = useDomainGroup(typed.length ? value : userDomain, enabled)
  const error = domainListError(value) || serverError
  const organisations = groups.filter(group => group.domains.length > 1)

  return (
    <Box>
      <TextField
        fullWidth
        size='small'
        label='Email domains'
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={`@${userDomain}`}
        error={!!error}
        helperText={error || 'To add more domains, put a semicolon between them. For example: @domain1.com; @domain2.com'}
        FormHelperTextProps={{ sx: { fontSize: '0.875rem', mx: 0 } }}
      />
      {!error && domains.length > 0 && (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.75 }}>
          Who can see the files: people on this Building Passport with an email address at{' '}
          <strong>{joinDomains(domains)}</strong>.
          {organisations.length > 0 && (
            <>
              {' '}{joinDomains(organisations.map(group => group.domain))}{' '}
              {organisations.length > 1 ? 'are' : 'is'} part of an organisation, so all of its domains are included.
            </>
          )}
        </Typography>
      )}
    </Box>
  )
}

export default DomainListField
